import { Button, Space, Typography } from 'antd';
import React from 'react';

import { PrevIcon as LeftOutlined, NextPageIcon as RightOutlined } from '@/icons';

/** All valid step keys in the 2-step assessment flow (numeric entry + review sub-steps). */
export type AssessmentFlowStep = 1 | '1-review' | 2 | '2-generating' | '2-review';

const ASSESSMENT_FLOW: Array<{ key: AssessmentFlowStep; label: string }> = [
  { key: 1, label: 'Step 1' },
  { key: '1-review', label: 'Step 1 - Review' },
  { key: 2, label: 'Step 2' },
  { key: '2-review', label: 'Step 2 - Review' },
];

const { Text } = Typography;

/** Props for the `AssessmentStepPager` prev/next navigation control. */
export interface AssessmentStepPagerProps {
  current: AssessmentFlowStep;
  onStepChange: (next: AssessmentFlowStep) => void;
  disableNext?: boolean;
}

/** Prev/next pagination control for moving between the 8 assessment flow steps. */
export const AssessmentStepPager: React.FC<AssessmentStepPagerProps> = ({
  current,
  onStepChange,
  disableNext = false,
}) => {
  const currentIndex = Math.max(
    0,
    ASSESSMENT_FLOW.findIndex((item) => item.key === current),
  );
  const currentItem = ASSESSMENT_FLOW[currentIndex] ?? ASSESSMENT_FLOW[0];
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < ASSESSMENT_FLOW.length - 1 && !disableNext;

  return (
    <Space align="center" size="small">
      <Button
        type="text"
        size="small"
        icon={<LeftOutlined />}
        aria-label="Previous step"
        disabled={!canPrev}
        onClick={() => {
          if (canPrev) {
            onStepChange(ASSESSMENT_FLOW[currentIndex - 1].key);
          }
        }}
      />
      <Text type="secondary">{currentItem.label}</Text>
      <Button
        type="text"
        size="small"
        icon={<RightOutlined />}
        aria-label="Next step"
        disabled={!canNext}
        onClick={() => {
          if (canNext) {
            onStepChange(ASSESSMENT_FLOW[currentIndex + 1].key);
          }
        }}
      />
    </Space>
  );
};

export default AssessmentStepPager;
